/**
 * Service for managing user places (private places stored in localStorage + encrypted file)
 *
 * Structure:
 * - localUuid: unique identifier for localStorage (generated client-side) - like contacts
 * - placeUuid: reference to places table UUID (optional - null for private places)
 * - role: 'CONTACT' (private), 'PUBLIC' (linked to DB), 'PRIVATE' (private establishment)
 * - placeTypeCode: reference to places_types.code (business category)
 * - isOwner: true if user claims ownership of this establishment
 */

import { getAuthHeaders } from './authService';
import { createPlace as createPublicPlace } from './placesService';

const API_BASE_URL = 'https://apicabaza.creago.be/rest/cabaza/places';
const STORAGE_KEY = 'cabaza_user_places';

// In-memory cache
let placesCache = {};

// Debounce timer for server sync
let syncDebounceTimer = null;
const SYNC_DEBOUNCE_MS = 2000; // 2 seconds debounce

/**
 * Generate a UUID v4
 * @returns {string} UUID
 */
const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

/**
 * Get places from localStorage with timestamp
 * @param {number} peopleId - User's ID
 * @returns {Object} { places: Array, lastModified: string|null }
 */
const getLocalPlacesWithTimestamp = (peopleId) => {
    try {
        const data = localStorage.getItem(STORAGE_KEY);
        const allData = data ? JSON.parse(data) : {};
        const userData = allData[peopleId];

        // Support both old format (array) and new format (object with timestamp)
        if (Array.isArray(userData)) {
            return { places: userData, lastModified: null };
        } else if (userData && typeof userData === 'object') {
            return {
                places: userData.places || [],
                lastModified: userData.lastModified || null
            };
        }
        return { places: [], lastModified: null };
    } catch (error) {
        console.error('Error getting local places:', error);
        return { places: [], lastModified: null };
    }
};

/**
 * Get places from localStorage (fallback) - backward compatible
 * @param {number} peopleId - User's ID
 * @returns {Array}
 */
const getLocalPlaces = (peopleId) => {
    return getLocalPlacesWithTimestamp(peopleId).places;
};

/**
 * Save places to localStorage with timestamp
 * @param {number} peopleId - User's ID
 * @param {Array} places - Places array
 * @param {string} lastModified - ISO timestamp (optional, defaults to now)
 */
const saveLocalPlaces = (peopleId, places, lastModified = null) => {
    try {
        const timestamp = lastModified || new Date().toISOString();
        console.log('=== SAVING PLACES TO LOCALSTORAGE ===');
        console.log('peopleId:', peopleId);
        console.log('places count:', places.length);
        console.log('timestamp:', timestamp);

        const data = localStorage.getItem(STORAGE_KEY);
        const allData = data ? JSON.parse(data) : {};
        allData[peopleId] = {
            places: places,
            lastModified: timestamp
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(allData));

        // Verify save
        const verify = localStorage.getItem(STORAGE_KEY);
        const verifyData = JSON.parse(verify);
        console.log('Verified localStorage save:', verifyData[peopleId]?.places?.length, 'places');
    } catch (error) {
        console.error('Error saving local places:', error);
    }
};

/**
 * Get local timestamp for a user
 * @param {number} peopleId - User's ID
 * @returns {string|null}
 */
const getLocalTimestamp = (peopleId) => {
    return getLocalPlacesWithTimestamp(peopleId).lastModified;
};

/**
 * Get places count from database (via people_places table)
 * @param {number} userId - User's ID
 * @returns {Promise<number>} Count of places linked to user in DB
 */
const getPlacesCountFromDB = async (userId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/count/${userId}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        const success = result.success || result.SUCCESS;
        const count = result.count || result.COUNT || 0;

        return success ? count : 0;
    } catch (error) {
        console.error('Error getting places count from DB:', error);
        return -1; // Return -1 to indicate error
    }
};

/**
 * Get current user's peopleUuid and storageKey from localStorage
 * @returns {Object} { peopleuuid, storageKey } or null
 */
const getCurrentUserStorageInfo = () => {
    try {
        const userJson = localStorage.getItem('cabaza_user');
        if (userJson) {
            const user = JSON.parse(userJson);
            return {
                peopleuuid: user.peopleUuid || user.peopleuuid || user.PEOPLEUUID,
                storageKey: user.storageKey || user.STORAGEKEY
            };
        }
    } catch (error) {
        console.error('Error getting user storage info:', error);
    }
    return null;
};

/**
 * Sync places TO server (debounced)
 * Saves current localStorage places to encrypted file on server
 * @param {number} userId - User's ID
 */
const syncPlacesToServerDebounced = (userId) => {
    // Clear existing timer
    if (syncDebounceTimer) {
        clearTimeout(syncDebounceTimer);
    }

    // Set new timer
    syncDebounceTimer = setTimeout(async () => {
        const storageInfo = getCurrentUserStorageInfo();
        if (!storageInfo?.peopleuuid || !storageInfo?.storageKey) {
            console.warn('Cannot sync places to server: missing peopleuuid or storageKey');
            return;
        }

        try {
            const localData = getLocalPlacesWithTimestamp(userId);
            const timestamp = localData.lastModified || new Date().toISOString();
            console.log('Syncing places to server...', localData.places.length, 'places, timestamp:', timestamp);

            const result = await savePlacesToFile(
                storageInfo.peopleuuid,
                storageInfo.storageKey,
                localData.places,
                timestamp
            );

            if (result.success || result.SUCCESS) {
                console.log('Places synced to server successfully');
            } else {
                console.error('Failed to sync places to server:', result.message || result.MESSAGE);
            }
        } catch (error) {
            console.error('Error syncing places to server:', error);
        }
    }, SYNC_DEBOUNCE_MS);
};

/**
 * Sync places with DB to check for deleted places and update links
 * Called after login to:
 * 1. Check if linked public places still exist (remove placeUuid if deleted)
 * 2. Update place data from DB if changed
 * 3. Link orphan places if a matching public place is found (by googlePlaceId or name+city)
 * @param {number} userId - User's ID
 * @param {Array} places - Places array to sync
 * @returns {Promise<Array>} Synced places with updated flags
 */
export const syncPlacesWithDB = async (userId, places) => {
    if (!userId || !places || places.length === 0) {
        return places;
    }

    try {
        console.log('Syncing places with DB to check public places...', places.length, 'places');

        const response = await fetch(`${API_BASE_URL}/sync`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
                userId,
                places
            })
        });

        if (!response.ok) {
            console.error('Sync places API error:', response.status);
            return places;
        }

        const result = await response.json();
        const success = result.success || result.SUCCESS;
        const syncedPlaces = result.places || result.PLACES || places;
        const changes = result.changes || result.CHANGES || 0;

        if (success) {
            console.log(`Places sync completed: ${changes} changes`);
            if (changes > 0) {
                // Update localStorage with synced places
                const timestamp = new Date().toISOString();
                saveLocalPlaces(userId, syncedPlaces, timestamp);
                placesCache[userId] = syncedPlaces;

                // Also sync to server file
                const storageInfo = getCurrentUserStorageInfo();
                if (storageInfo?.peopleuuid && storageInfo?.storageKey) {
                    await savePlacesToFile(
                        storageInfo.peopleuuid,
                        storageInfo.storageKey,
                        syncedPlaces,
                        timestamp
                    );
                }
            }
            return syncedPlaces;
        }

        return places;
    } catch (error) {
        console.error('Error syncing places with DB:', error);
        return places;
    }
};

/**
 * Normalize place data - includes ALL form fields
 * @param {Object} place - Raw place data
 * @returns {Object} Normalized place
 */
const normalizePlace = (place) => ({
    // === IDENTIFIERS ===
    // Local UUID (client-side, like contacts)
    localUuid: place.localUuid || place.localuuid || place.LOCALUUID || place.localId || place.localid || place.LOCALID || generateUUID(),
    // Reference to public place UUID in DB (if any)
    placeUuid: place.placeUuid || place.placeuuid || place.PLACEUUID || place.publicPlaceId || place.publicplaceid || place.PUBLICPLACEID || null,
    // Legacy support - keep publicPlaceId for backward compatibility
    publicPlaceId: place.publicPlaceId || place.publicplaceid || place.PUBLICPLACEID || null,

    // === ROLE & OWNERSHIP ===
    // Role: CONTACT (private contact), PUBLIC (linked to DB), PRIVATE (private establishment)
    // Migration: placeType -> role
    role: place.role || place.ROLE || place.placeType || place.placetype || place.PLACETYPE || 'CONTACT',
    // Is user the owner of this establishment?
    isOwner: place.isOwner || place.isowner || place.ISOWNER || false,
    // Relationship type: owner, employee, worker, client, visitor
    relationshipType: place.relationshipType || place.relationshiptype || place.RELATIONSHIPTYPE || '',
    // Legal form
    legalForm: place.legalForm || place.legalform || place.LEGALFORM || '',

    // === BASIC INFO ===
    placeName: place.placeName || place.placename || place.PLACENAME || '',
    // Reference to places_types.code (business category: restaurant, shop, etc.)
    placeTypeCode: place.placeTypeCode || place.placetypecode || place.PLACETYPECODE || place.placeType || place.placetype || place.PLACETYPE || '',
    category: place.category || place.CATEGORY || 'favorite',

    // === GOOGLE DATA ===
    googlePlaceId: place.googlePlaceId || place.googleplaceid || place.GOOGLEPLACEID || '',
    googleUrl: place.googleUrl || place.googleurl || place.GOOGLEURL || '',

    // === ADDRESS COMPONENTS ===
    street: place.street || place.STREET || '',
    number: place.number || place.NUMBER || '',
    box: place.box || place.BOX || '',
    postalCode: place.postalCode || place.postalcode || place.POSTALCODE || '',
    city: place.city || place.CITY || '',
    country: place.country || place.COUNTRY || '',
    countryCode: place.countryCode || place.countrycode || place.COUNTRYCODE || '',

    // === COORDINATES ===
    latitude: place.latitude || place.LATITUDE || null,
    longitude: place.longitude || place.LONGITUDE || null,

    // === CONTACT INFO ===
    phone: place.phone || place.PHONE || '',
    email: place.email || place.EMAIL || '',
    website: place.website || place.WEBSITE || '',
    contactPerson: place.contactPerson || place.contactperson || place.CONTACTPERSON || '',

    // === BUSINESS INFO ===
    tvaNum: place.tvaNum || place.tvanum || place.TVANUM || '',
    bankNum: place.bankNum || place.banknum || place.BANKNUM || '',
    firmType: place.firmType || place.firmtype || place.FIRMTYPE || '',
    description: place.description || place.DESCRIPTION || '',
    contactName: place.contactName || place.contactname || place.CONTACTNAME || place.contactPerson || place.contactperson || place.CONTACTPERSON || '',

    // === SOCIAL MEDIA ===
    facebookUrl: place.facebookUrl || place.facebookurl || place.FACEBOOKURL || '',
    twitterUrl: place.twitterUrl || place.twitterurl || place.TWITTERURL || '',
    youtubeUrl: place.youtubeUrl || place.youtubeurl || place.YOUTUBEURL || '',

    // === MEDIA ===
    imageUrl: place.imageUrl || place.imageurl || place.IMAGEURL || '',

    // === FLAGS ===
    isFavorite: place.isFavorite || place.isfavorite || place.ISFAVORITE || place.addToFavorites || false,
    isActive: place.isActive !== undefined ? place.isActive : (place.isactive !== undefined ? place.isactive : (place.status !== undefined ? place.status : true)),
    parking: place.parking || place.PARKING || false,

    // === TIMESTAMPS ===
    createdAt: place.createdAt || place.createdat || place.CREATEDAT || new Date().toISOString(),
    updatedAt: place.updatedAt || place.updatedat || place.UPDATEDAT || new Date().toISOString(),

    // === CREATOR ===
    createdByPeopleId: place.createdByPeopleId || place.createdbypeopleid || place.CREATEDBYPEOPLEID || null,
    createdByName: place.createdByName || place.createdbyname || place.CREATEDBYNAME || ''
});

/**
 * Save places to encrypted file on server with timestamp
 * @param {string} peopleuuid - Owner's peopleUuid
 * @param {string} storageKey - Encryption key
 * @param {Array} places - Places to save
 * @param {string} lastModified - ISO timestamp
 * @returns {Promise<Object>} Result with success status
 */
export const savePlacesToFile = async (peopleuuid, storageKey, places, lastModified = null) => {
    try {
        const timestamp = lastModified || new Date().toISOString();
        const response = await fetch(`${API_BASE_URL}/save-file`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
                peopleuuid,
                storageKey,
                places,
                lastModified: timestamp
            })
        });

        if (!response.ok) {
            const text = await response.text();
            console.error('Save places file error response:', text);
            return { success: false, message: `HTTP ${response.status}` };
        }

        const result = await response.json();
        return result;
    } catch (error) {
        console.error('Error saving places to file:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Load places from encrypted file on server with timestamp
 * @param {string} peopleuuid - Owner's peopleUuid
 * @param {string} storageKey - Decryption key
 * @returns {Promise<Object>} { places: Array, lastModified: string|null }
 */
export const loadPlacesFromFileWithTimestamp = async (peopleuuid, storageKey) => {
    try {
        console.log('=== loadPlacesFromFile ===');
        console.log('peopleuuid:', peopleuuid);

        const response = await fetch(`${API_BASE_URL}/load-file`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
                peopleuuid,
                storageKey
            })
        });

        console.log('Response status:', response.status);

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Load places file error response:', errorText);
            return { places: [], lastModified: null };
        }

        const text = await response.text();
        let result;
        try {
            result = JSON.parse(text);
        } catch (parseErr) {
            console.error('JSON parse error:', parseErr);
            return { places: [], lastModified: null };
        }

        const success = result.success || result.SUCCESS;
        const places = result.places || result.PLACES || [];
        const lastModified = result.lastModified || result.LASTMODIFIED || null;

        console.log('Loaded places from file:', places.length, 'places');

        return success ? { places, lastModified } : { places: [], lastModified: null };
    } catch (error) {
        console.error('Error loading places from file:', error);
        return { places: [], lastModified: null };
    }
};

/**
 * Load places from encrypted file on server (backward compatible)
 * @param {string} peopleuuid - Owner's peopleUuid
 * @param {string} storageKey - Decryption key
 * @returns {Promise<Array>} Places array
 */
export const loadPlacesFromFile = async (peopleuuid, storageKey) => {
    const result = await loadPlacesFromFileWithTimestamp(peopleuuid, storageKey);
    return result.places;
};

/**
 * Sync places FROM server on login
 *
 * NEW LOGIC: people_places is the MASTER table (source of truth)
 *
 * Flow:
 * 1. Send local places (localStorage) to API
 * 2. API adds missing CONTACT places to people_places
 * 3. API returns ALL places from people_places (master)
 * 4. Update localStorage and fileserver (caches) with master data
 *
 * @param {number} userId - User's ID
 * @param {string} peopleuuid - User's peopleUuid
 * @param {string} storageKey - User's storageKey for decryption
 * @returns {Promise<Array>} Loaded places
 */
// Debug info for sync - exposed globally for debug panel
let lastSyncDebug = null;
export const getLastSyncDebug = () => lastSyncDebug;

export const syncPlacesFromServer = async (userId, peopleuuid, storageKey) => {
    // Initialize debug info
    const debug = {
        timestamp: new Date().toISOString(),
        userId,
        steps: [],
        localCount: 0,
        serverFileCount: 0,
        masterCount: 0,
        contactsAdded: 0,
        finalCount: 0,
        source: 'people_places (master)',
        errors: []
    };

    const addDebug = (step, details = {}) => {
        debug.steps.push({ step, details, time: new Date().toISOString() });
        console.log(`[PLACES SYNC] ${step}`, details);
    };

    // Helper to save debug and show popup
    const finishDebug = (places = []) => {
        debug.finalCount = places.length;
        lastSyncDebug = debug;
        console.log('[PLACES SYNC] finishDebug called, places:', places.length);
        console.log('[PLACES SYNC] window.showPlacesSyncDebug available:', typeof window.showPlacesSyncDebug);
        // Always trigger the popup if available
        if (window.showPlacesSyncDebug) {
            console.log('[PLACES SYNC] Calling window.showPlacesSyncDebug...');
            window.showPlacesSyncDebug(places);
        } else {
            console.warn('[PLACES SYNC] window.showPlacesSyncDebug NOT available!');
        }
        return places;
    };

    if (!userId || !peopleuuid || !storageKey) {
        addDebug('ERROR: Missing params', { userId, peopleuuid: !!peopleuuid, storageKey: !!storageKey });
        debug.errors.push('Missing required params');
        return finishDebug([]);
    }

    try {
        addDebug('Starting sync (people_places = MASTER)...');

        // Step 1: Get local places to send to API (so it can add missing CONTACTS)
        const localData = getLocalPlacesWithTimestamp(userId);
        debug.localCount = localData.places.length;
        addDebug('Local places loaded', { count: localData.places.length });

        // Step 2: Call sync API - sends local places, receives master list from people_places
        addDebug('Calling /sync API (master = people_places)...');

        const response = await fetch(`${API_BASE_URL}/sync`, {
            method: 'POST',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify({
                userId,
                places: localData.places // Send local places so API can add missing CONTACTS
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            addDebug('API error', { status: response.status, error: errorText });
            debug.errors.push(`API error: ${response.status}`);

            // Fallback: use local data
            const localPlaces = localData.places.map(normalizePlace);
            placesCache[userId] = localPlaces;
            return finishDebug(localPlaces);
        }

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        const masterPlaces = result.places ?? result.PLACES ?? [];
        const contactsAdded = result.contactsAdded ?? result.CONTACTSADDED ?? 0;
        const message = result.message ?? result.MESSAGE ?? '';

        debug.masterCount = masterPlaces.length;
        debug.contactsAdded = contactsAdded;

        addDebug('API response', {
            success,
            masterCount: masterPlaces.length,
            contactsAdded,
            message,
            placeNames: masterPlaces.map(p => p.placeName || p.PLACENAME || 'N/A')
        });

        if (!success) {
            addDebug('API returned success=false', { message });
            debug.errors.push(`API failed: ${message}`);

            // Fallback: use local data
            const localPlaces = localData.places.map(normalizePlace);
            placesCache[userId] = localPlaces;
            return finishDebug(localPlaces);
        }

        // Step 3: Normalize master places
        const normalizedPlaces = masterPlaces.map(p => normalizePlace(p));
        const timestamp = new Date().toISOString();

        // Step 4: Update caches (localStorage and fileserver)
        addDebug('Updating caches with master data...');

        // Update memory cache
        placesCache[userId] = normalizedPlaces;

        // Update localStorage
        saveLocalPlaces(userId, normalizedPlaces, timestamp);

        // Update fileserver (async, don't block)
        savePlacesToFile(peopleuuid, storageKey, normalizedPlaces, timestamp)
            .then(() => addDebug('Fileserver cache updated'))
            .catch(err => {
                addDebug('Fileserver update failed', { error: err.message });
                debug.errors.push(`Fileserver: ${err.message}`);
            });

        addDebug('Sync completed successfully', {
            finalCount: normalizedPlaces.length,
            contactsAdded
        });

        return finishDebug(normalizedPlaces);

    } catch (error) {
        console.error('Error syncing places from server:', error);
        debug.errors.push(`Main error: ${error.message}`);
        addDebug('FATAL ERROR', { error: error.message });

        // Fallback: use local data
        const localPlaces = getLocalPlaces(userId).map(normalizePlace);
        placesCache[userId] = localPlaces;
        return finishDebug(localPlaces);
    }
};

/**
 * Get all user places (private + linked to public)
 * @param {number} peopleId - User's ID
 * @param {boolean} forceRefresh - Force refresh (skip cache)
 * @returns {Promise<Array>} Array of place objects
 */
export const getUserPlaces = async (peopleId, forceRefresh = false) => {
    // Return from cache if available and not forcing refresh
    if (!forceRefresh && placesCache[peopleId]) {
        console.log('Returning cached places for peopleId:', peopleId);
        return placesCache[peopleId];
    }

    try {
        // Get local places
        let localPlaces = getLocalPlaces(peopleId);

        // If localStorage is empty, try to load from server file first
        if (localPlaces.length === 0) {
            const storageInfo = getCurrentUserStorageInfo();
            if (storageInfo?.peopleuuid && storageInfo?.storageKey) {
                console.log('localStorage empty, loading places from server file...');
                const serverPlaces = await loadPlacesFromFile(storageInfo.peopleuuid, storageInfo.storageKey);
                if (serverPlaces && serverPlaces.length > 0) {
                    console.log('Loaded', serverPlaces.length, 'places from server file');
                    localPlaces = serverPlaces;
                    saveLocalPlaces(peopleId, serverPlaces);
                }
            }
        }

        // Normalize places
        const places = localPlaces.map(normalizePlace);

        // Update cache
        placesCache[peopleId] = places;

        return places;
    } catch (error) {
        console.error('Error getting user places:', error);
        return getLocalPlaces(peopleId).map(normalizePlace);
    }
};

/**
 * Get visible places (excluding PRIVATE type if needed)
 * @param {number} peopleId - User's ID
 * @param {boolean} forceRefresh - Force refresh
 * @returns {Promise<Array>} Array of visible place objects
 */
export const getVisibleUserPlaces = async (peopleId, forceRefresh = false) => {
    const allPlaces = await getUserPlaces(peopleId, forceRefresh);
    // For now, return all places (CONTACT and PUBLIC are both visible to owner)
    return allPlaces;
};

/**
 * Add a new place for user (private by default)
 * @param {number} userId - User's ID
 * @param {Object} placeData - Place data to add
 * @returns {Promise<Object>} Created place or null
 */
export const addUserPlace = async (userId, placeData) => {
    try {
        // Generate localUuid if not provided
        const localUuid = placeData.localUuid || placeData.localId || generateUUID();

        // Build place object
        const newPlace = normalizePlace({
            ...placeData,
            localUuid: localUuid,
            role: placeData.placeUuid || placeData.publicPlaceId ? 'PUBLIC' : 'CONTACT',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdByPeopleId: placeData.createdByPeopleId || userId,
            createdByName: placeData.createdByName || ''
        });

        // Initialize cache if empty
        if (!placesCache[userId]) {
            placesCache[userId] = getLocalPlaces(userId).map(normalizePlace);
        }

        // Check for duplicates (by googlePlaceId or placeName + city)
        const isDuplicate = placesCache[userId].some(p =>
            (p.googlePlaceId && p.googlePlaceId === newPlace.googlePlaceId) ||
            (p.placeName === newPlace.placeName && p.city === newPlace.city && p.street === newPlace.street)
        );

        if (isDuplicate) {
            console.log('Place already exists:', newPlace.placeName);
            // Return existing place
            return placesCache[userId].find(p =>
                (p.googlePlaceId && p.googlePlaceId === newPlace.googlePlaceId) ||
                (p.placeName === newPlace.placeName && p.city === newPlace.city && p.street === newPlace.street)
            );
        }

        // Add to cache
        placesCache[userId].push(newPlace);
        saveLocalPlaces(userId, placesCache[userId]);

        // Sync to server (debounced)
        syncPlacesToServerDebounced(userId);

        console.log('Added new place:', newPlace.placeName, 'localUuid:', newPlace.localUuid);
        return newPlace;
    } catch (error) {
        console.error('Error adding place:', error);
        return null;
    }
};

/**
 * Add a place from Google Places data
 * @param {number} userId - User's ID
 * @param {Object} googlePlaceData - Data from Google Places API
 * @returns {Promise<Object>} Created place
 */
export const addPlaceFromGoogle = async (userId, googlePlaceData) => {
    return addUserPlace(userId, {
        placeName: googlePlaceData.placeName || googlePlaceData.name,
        googlePlaceId: googlePlaceData.googlePlaceId || googlePlaceData.place_id || '',
        street: googlePlaceData.street || '',
        number: googlePlaceData.number || '',
        box: googlePlaceData.box || '',
        postalCode: googlePlaceData.postalCode || '',
        city: googlePlaceData.city || '',
        country: googlePlaceData.country || '',
        countryCode: googlePlaceData.countryCode || '',
        latitude: googlePlaceData.latitude || googlePlaceData.lat,
        longitude: googlePlaceData.longitude || googlePlaceData.lng,
        category: 'favorite',
        isOwner: false // User is not owner by default
    });
};

/**
 * Update a place
 * @param {number} userId - User's ID
 * @param {string} localUuid - Place localUuid
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated place or null
 */
export const updateUserPlace = async (userId, localUuid, updates) => {
    try {
        if (!placesCache[userId]) {
            placesCache[userId] = getLocalPlaces(userId).map(normalizePlace);
        }

        // Support both localUuid and legacy localId
        const placeIndex = placesCache[userId].findIndex(p => p.localUuid === localUuid || p.localId === localUuid);
        if (placeIndex === -1) {
            console.error('Place not found:', localUuid);
            return null;
        }

        // Update place
        placesCache[userId][placeIndex] = {
            ...placesCache[userId][placeIndex],
            ...updates,
            updatedAt: new Date().toISOString()
        };

        saveLocalPlaces(userId, placesCache[userId]);
        syncPlacesToServerDebounced(userId);

        return placesCache[userId][placeIndex];
    } catch (error) {
        console.error('Error updating place:', error);
        return null;
    }
};

/**
 * Remove a place
 * @param {number} userId - User's ID
 * @param {string} localUuid - Place localUuid to remove
 * @returns {Promise<boolean>} Success status
 */
export const removeUserPlace = async (userId, localUuid) => {
    try {
        if (!placesCache[userId]) {
            placesCache[userId] = getLocalPlaces(userId).map(normalizePlace);
        }

        const initialLength = placesCache[userId].length;
        // Support both localUuid and legacy localId
        placesCache[userId] = placesCache[userId].filter(p => p.localUuid !== localUuid && p.localId !== localUuid);

        if (placesCache[userId].length === initialLength) {
            console.error('Place not found:', localUuid);
            return false;
        }

        saveLocalPlaces(userId, placesCache[userId]);
        syncPlacesToServerDebounced(userId);

        console.log('Removed place:', localUuid);
        return true;
    } catch (error) {
        console.error('Error removing place:', error);
        return false;
    }
};

/**
 * Make a private place public (save to DB)
 * User must claim ownership first
 * @param {number} userId - User's ID
 * @param {string} localUuid - Place localUuid
 * @returns {Promise<Object>} Result with success status and public place data
 */
export const makePublicPlace = async (userId, localUuid) => {
    try {
        if (!placesCache[userId]) {
            placesCache[userId] = getLocalPlaces(userId).map(normalizePlace);
        }

        // Support both localUuid and legacy localId
        const place = placesCache[userId].find(p => p.localUuid === localUuid || p.localId === localUuid);
        if (!place) {
            return { success: false, message: 'Place not found' };
        }

        if (!place.isOwner) {
            return { success: false, message: 'You must be the owner to make this place public' };
        }

        if (place.placeUuid || place.publicPlaceId) {
            return { success: false, message: 'Place is already public' };
        }

        // Create public place in DB
        const publicPlaceData = {
            placeName: place.placeName,
            googlePlaceId: place.googlePlaceId,
            street: place.street,
            number: place.number,
            box: place.box,
            postalCode: place.postalCode,
            city: place.city,
            country: place.country,
            countryCode: place.countryCode,
            latitude: place.latitude,
            longitude: place.longitude,
            phone: place.phone,
            email: place.email,
            website: place.website,
            description: place.description,
            placeTypeCode: place.placeTypeCode || '',
            tvaNum: place.tvaNum,
            createdById: userId
        };

        const result = await createPublicPlace(publicPlaceData);

        if (result.success && result.data) {
            // Update local place with public UUID
            const publicPlaceId = result.data.id || result.data.ID;
            const placeUuid = result.data.placeUuid || result.data.PLACEUUID || null;
            await updateUserPlace(userId, localUuid, {
                publicPlaceId: publicPlaceId,
                placeUuid: placeUuid,
                role: 'PUBLIC'
            });

            return {
                success: true,
                message: 'Place is now public',
                data: result.data,
                publicPlaceId: publicPlaceId,
                placeUuid: placeUuid
            };
        }

        return { success: false, message: result.message || 'Failed to create public place' };
    } catch (error) {
        console.error('Error making place public:', error);
        return { success: false, message: error.message };
    }
};

/**
 * Claim ownership of a place
 * @param {number} userId - User's ID
 * @param {string} localUuid - Place localUuid
 * @param {boolean} isOwner - Whether user is owner
 * @returns {Promise<Object>} Updated place
 */
export const setPlaceOwnership = async (userId, localUuid, isOwner) => {
    return updateUserPlace(userId, localUuid, { isOwner });
};

/**
 * Search user's private places
 * @param {number} userId - User's ID
 * @param {string} keyword - Search term
 * @param {number} limit - Max results
 * @returns {Promise<Array>} Matching places
 */
export const searchUserPlaces = async (userId, keyword, limit = 10) => {
    if (!keyword || keyword.length < 2) {
        return [];
    }

    const places = await getUserPlaces(userId);
    const lowerKeyword = keyword.toLowerCase();

    return places
        .filter(p =>
            p.placeName?.toLowerCase().includes(lowerKeyword) ||
            p.street?.toLowerCase().includes(lowerKeyword) ||
            p.city?.toLowerCase().includes(lowerKeyword)
        )
        .slice(0, limit);
};

/**
 * Get place by localUuid (or legacy localId)
 * @param {number} userId - User's ID
 * @param {string} localUuid - Place localUuid
 * @returns {Object|null} Place or null
 */
export const getPlaceByLocalUuid = (userId, localUuid) => {
    const places = placesCache[userId] || getLocalPlaces(userId);
    return places.find(p => p.localUuid === localUuid || p.localId === localUuid) || null;
};

// Legacy alias
export const getPlaceByLocalId = getPlaceByLocalUuid;

/**
 * Get place by placeUuid (or legacy publicPlaceId)
 * @param {number} userId - User's ID
 * @param {string} placeUuid - Public place UUID from DB
 * @returns {Object|null} Place or null
 */
export const getPlaceByPlaceUuid = (userId, placeUuid) => {
    const places = placesCache[userId] || getLocalPlaces(userId);
    return places.find(p => p.placeUuid === placeUuid || p.publicPlaceId === placeUuid) || null;
};

// Legacy alias
export const getPlaceByPublicId = getPlaceByPlaceUuid;

/**
 * Get count of user's places
 * @param {number} peopleId - User's ID
 * @returns {number} Number of places
 */
export const getUserPlacesCount = (peopleId) => {
    const places = placesCache[peopleId] || getLocalPlaces(peopleId);
    return places.length;
};

/**
 * Clear cache (useful on logout)
 * @param {number} peopleId - User's ID (optional)
 */
export const clearPlacesCache = (peopleId = null) => {
    if (peopleId) {
        delete placesCache[peopleId];
    } else {
        placesCache = {};
    }
};

/**
 * Preload places for a user
 * @param {number} peopleId - User's ID
 */
export const preloadPlaces = async (peopleId) => {
    await getUserPlaces(peopleId);
};

export default {
    getUserPlaces,
    getVisibleUserPlaces,
    addUserPlace,
    addPlaceFromGoogle,
    updateUserPlace,
    removeUserPlace,
    makePublicPlace,
    setPlaceOwnership,
    searchUserPlaces,
    getPlaceByLocalUuid,
    getPlaceByLocalId, // Legacy alias
    getPlaceByPlaceUuid,
    getPlaceByPublicId, // Legacy alias
    getUserPlacesCount,
    clearPlacesCache,
    preloadPlaces,
    syncPlacesFromServer,
    savePlacesToFile,
    loadPlacesFromFile
};
